<template>
  <section class="candidate financial assessment">
    <v-card class="pa-4 mt-4">
      <v-card-title>
        <div>
          <div class="title pb-1">
            Household Income Assessment
          </div>
          <div :class="themes.candidate.light.mutedText">
            <span v-if="financial.complete">
              <v-icon :color="themes.candidate.light.success">mdi-check-circle</v-icon> Complete
            </span>
            <span v-else-if="currencySet" class="error--text">
              <v-icon :color="themes.candidate.light.error">mdi-alert-outline</v-icon>
              You must add <strong>BOTH</strong> your parents and any other household income provider. 
              In the event that a parent does not support you provide an affidavit indicating this.
            </span>
            <span v-else>
              <v-icon>mdi-alert-outline</v-icon>
              Set the currency and number of dependents
            </span>
          </div>
        </div>
      </v-card-title>

      <v-card-text>
        <v-container grid-list-md class="pa-0">
          <v-layout row wrap fill-height align-center>
            <v-flex xs12 sm6>
              <v-autocomplete
                label="Currency"
                :value="financial.currency"
                @input="setCurrency"
                :items="currencies"
                :color="themes.candidate.light.input"
                :menu-props="{ offsetY: true }"
                attach
              />
            </v-flex>
            <v-flex xs12 sm6>
              <v-select
                label="Dependents"
                :value="financial.dependents"
                @input="setDependents"
                :items="[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15]"
                :color="themes.candidate.light.input"
                :menu-props="{ offsetY: true }"
                attach
              />
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>

      <v-card-text v-if="financial.incomes">
        <template v-for="income in financial.incomes">
          <HouseholdIncome
            :key="`hh-income-${income.id}`"
            :currencyDisplay="currencyDisplay"
            :income="income"
            @refresh="$emit('refresh')"
            @destroy="destroy(income)"
            @edit="edit(income)"
          />
        </template>
      </v-card-text>

      <v-card-text class="text-xs-center">
        <v-dialog
          v-if="financial.currency"
          v-model="dialog"
          attach=".candidate.financial.assessment"
          max-width="900px"
        >
          <v-chip slot="activator">
            <v-avatar>
              <v-btn
                slot="activator"
                :color="themes.candidate.light.secondaryActionButton"
                small
                fab
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </v-avatar>
            Add a income provider
          </v-chip>
          <v-card>
            <v-card-title class="title" :class="themes.candidate.light.dialogTitle">
              Additional household member
            </v-card-title>
            <v-form v-model="valid">
              <v-card-text>
                <v-select
                  label="Relationship"
                  v-model="income.relationship"
                  :color="themes.candidate.light.input"
                  :menu-props="{ offsetY: true }"
                  :rules="[rules.required]"
                  :items="relationships"
                  required
                  attach
                />
                <v-text-field
                  label="First Name"
                  v-model="income.givenName"
                  :rules="[rules.required, rules.counter]"
                  :color="themes.candidate.light.input"
                  counter="32"
                  required
                />
                <v-text-field
                  label="Surname"
                  v-model="income.familyName"
                  :rules="[rules.required, rules.counter]"
                  :color="themes.candidate.light.input"
                  counter="32"
                  required
                />
                <v-text-field
                  label="Occupation"
                  v-model="income.occupation"
                  :rules="[rules.required, rules.counter]"
                  :color="themes.candidate.light.input"
                  counter="32"
                  required
                />
                <v-text-field
                  label="Gross Monthly Income"
                  v-model="income.grossIncome"
                  :color="themes.candidate.light.input"
                  :rules="[rules.requiredNumber]"
                  :prefix="currencyDisplay"
                  hint="Income before taxes and expenses"
                  type="number"
                  required
                />
                <v-select
                  label="Type of Proof"
                  v-model="income.typeOfProof"
                  :color="themes.candidate.light.input"
                  :menu-props="{ offsetY: true }"
                  :rules="[rules.required]"
                  :items="proofs"
                  required
                  attach
                />
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn flat :color="themes.candidate.light.cancel" @click="dialog = false">
                  Cancel
                </v-btn>
                <v-btn
                  :disabled="!valid"
                  @click="submit"
                  :color="themes.candidate.light.primaryActionButton"
                >
                  Save
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
      </v-card-text>
    </v-card>
  </section>
</template>

<script>
// Data
import currencies from '@/data/currencies'

const blankIncome = {
  id: null,
  relationship: null,
  givenName: '',
  familyName: '',
  occupation: '',
  grossIncome: 0,
  typeOfProof: '',
}

const relationships = ['Father', 'Mother', 'Guardian', 'Grandfather', 'Grandmother']

const proofs = ['Affidavit', 'Death Certificate', 'Income Statement', 'Payslip']

// Components
import HouseholdIncome from '@/components/candidate/applications/HouseholdIncome'

// Vue Component
export default {
  props: {
    financial: {
      type: Object,
      required: true,
    },
  },
  components: { HouseholdIncome },
  created() {
    this.currencies = Object.values(currencies).map(c => ({ value: c.iso.code, text: c.name }))
  },
  data() {
    return {
      // Model
      dialog: false,
      income: Object.assign({ financialSurveyId: this.financial.id }, blankIncome),
      // Validation
      valid: false,
      rules: {
        required: v => !!v || 'must be provided',
        requiredNumber: v => v === 0 || !!v || 'must be provided',
        counter: value => value.length <= 32 || 'Max 32 characters',
      },
      // Data
      currencies: [],
      relationships,
      proofs,
    }
  },
  computed: {
    currencySet() {
      return this.financial.currency && this.financial.currency.length == 3
    },
    currencyDisplay() {
      if (this.currencySet) {
        return currencies[this.financial.currency].symbol.default.display
      } else {
        return ''
      }
    },
  },
  methods: {
    submit() {
      if (this.income.id) {
        this.$http
          .put(`/candidate/financial_survey/household_incomes/${this.income.id}`, {
            householdIncome: this.income,
          })
          .then(response => {
            this.dialog = false
            this.income = Object.assign({ financialSurveyId: this.financial.id }, blankIncome)
            this.$emit('refresh')
          })
      } else {
        this.$http
          .post('/candidate/financial_survey/household_incomes', { householdIncome: this.income })
          .then(response => {
            this.dialog = false
            this.income = Object.assign({ financialSurveyId: this.financial.id }, blankIncome)
            this.$emit('refresh')
          })
      }
    },
    edit(income) {
      this.income = income
      this.dialog = true
    },
    destroy(income) {
      this.$http
        .delete(`/candidate/financial_survey/household_incomes/${income.id}`)
        .then(() => this.$emit('refresh'))
    },
    setCurrency(code) {
      this.$http
        .put('/candidate/financial_survey', { financialSurvey: { currency: code } })
        .then(() => {
          this.financial.currency = code
        })
    },
    setDependents(number) {
      this.$http.put('/candidate/financial_survey', { financialSurvey: { dependents: number } })
    },
  },
}
</script>
